import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ScholarshipResultReport() {
  const [programs, setPrograms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [yearSemesters, setYearSemesters] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showError = (message) => {
    setErrorMessage(message);
  };

  const fetchPrograms = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/scholarship-result-report/programs/${clientId}/${collegeId}`,
        { headers: getAuthHeaders() }
      );
      setPrograms(res.data.data || []);
    } catch (err) {
      setPrograms([]);
      showError("Unable to load programs. Please check the server connection.");
    } finally {
      setLoading(false);
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    if (!clientId || !collegeId) return;

    setLoading(true);
    const batchUrl = selectedProgram
      ? `${API_BASE_URL}/api/scholarship-result-report/batches/${clientId}/${collegeId}/${selectedProgram}`
      : `${API_BASE_URL}/api/scholarship-result-report/batches/${clientId}/${collegeId}`;

    axios
      .get(batchUrl, { headers: getAuthHeaders() })
      .then((res) => setBatches(res.data.data || []))
      .catch(() => {
        setBatches([]);
        showError("Unable to load admitted batches.");
      })
      .finally(() => setLoading(false));

    setSelectedBatch("");
    setSelectedYear("");
    setSelectedSemester("");
    setYearSemesters([]);
    setRows([]);
  }, [selectedProgram, clientId, collegeId]);

  useEffect(() => {
    if (!selectedBatch || !clientId || !collegeId) return;

    setLoading(true);
    const yearSemesterUrl = selectedProgram
      ? `${API_BASE_URL}/api/scholarship-result-report/year-semesters/${clientId}/${collegeId}/${selectedProgram}/${selectedBatch}`
      : `${API_BASE_URL}/api/scholarship-result-report/year-semesters/${clientId}/${collegeId}/${selectedBatch}`;

    axios
      .get(yearSemesterUrl, { headers: getAuthHeaders() })
      .then((res) => setYearSemesters(res.data.data || []))
      .catch(() => {
        setYearSemesters([]);
        showError("Unable to load year and semester options.");
      })
      .finally(() => setLoading(false));

    setSelectedYear("");
    setSelectedSemester("");
    setRows([]);
  }, [selectedProgram, selectedBatch, clientId, collegeId]);

  const filteredSemesters = useMemo(() => {
    if (!selectedYear) return [];
    return yearSemesters.filter((item) => String(item.year_number) === String(selectedYear));
  }, [yearSemesters, selectedYear]);

  const validateFilters = () => {
    if (!selectedBatch || !selectedYear || !selectedSemester) {
      showError("Please select admitted batch, year, and semester.");
      return false;
    }
    return true;
  };

  const requestPayload = () => ({
    client_id: clientId,
    college_id: collegeId,
    ...(selectedProgram ? { program_id: selectedProgram } : {}),
    admittedbatch_id: selectedBatch,
    year_number: selectedYear,
    semester_number: selectedSemester,
  });

  const handleGetReport = async () => {
    setErrorMessage("");
    if (!validateFilters()) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/scholarship-result-report/report`, {
        params: requestPayload(),
        headers: getAuthHeaders(),
      });
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
      showError(err.response?.data?.message || "Unable to load scholarship result report.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setErrorMessage("");
    if (!validateFilters()) return;

    setDownloadLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/scholarship-result-report/download-excel`,
        requestPayload(),
        {
          headers: getAuthHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Scholarship Result Report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError(err.response?.data?.message || "Unable to download scholarship result Excel.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const columns = [
    { field: "s_no", headerName: "Sno", width: 80 },
    { field: "program_name", headerName: "Program", minWidth: 180, flex: 1 },
    { field: "hallticket_number", headerName: "HallticketNumber", width: 170 },
    { field: "student_name", headerName: "StudentName", minWidth: 240, flex: 1 },
    { field: "gender", headerName: "Gender", width: 100 },
    { field: "semester", headerName: "Semester", width: 110 },
    { field: "exam_fee_paid", headerName: "ExamFeePaid", width: 140 },
    { field: "maximum_marks", headerName: "MaximumMarks", width: 140 },
    { field: "marks_obtained", headerName: "MarksObtained", width: 140 },
    { field: "percentage_cgpa", headerName: "Percentage/CGPA", width: 150 },
    { field: "grade", headerName: "Grade", width: 100 },
  ];

  const gridRows = rows.map((row, index) => ({
    id: `${row.hallticket_number || index}-${index}`,
    s_no: index + 1,
    ...row,
  }));

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography variant="h6">Scholarship Result Report</Typography>
      </Toolbar>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Program"
          select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">All Programs</option>
          {programs.map((program) => (
            <option key={program.program_id} value={program.program_id}>
              {program.program_name}
            </option>
          ))}
        </TextField>

        <TextField
          label="Admitted Batch"
          select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Batch</option>
          {batches.map((batch) => (
            <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
              {batch.admittedbatch}
            </option>
          ))}
        </TextField>

        <TextField
          label="Year"
          select
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(e.target.value);
            setSelectedSemester("");
          }}
          size="small"
          sx={{ minWidth: 120 }}
          disabled={!selectedBatch}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Year</option>
          {[...new Set(yearSemesters.map((item) => item.year_number))].map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </TextField>

        <TextField
          label="Semester"
          select
          value={selectedSemester}
          onChange={(e) => setSelectedSemester(e.target.value)}
          size="small"
          sx={{ minWidth: 140 }}
          disabled={!selectedYear}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Semester</option>
          {filteredSemesters.map((item) => (
            <option
              key={`${item.year_number}-${item.semester_number}`}
              value={item.semester_number}
            >
              {item.semester_number}
            </option>
          ))}
        </TextField>

        <Button
          variant="contained"
          onClick={handleGetReport}
          disabled={loading}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : "Get Report"}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadExcel}
          disabled={downloadLoading}
          sx={{ color: "#fff", minWidth: 150 }}
        >
          {downloadLoading ? <CircularProgress color="inherit" size={20} /> : "Download Excel"}
        </Button>
      </Box>

      <Box sx={{ height: 520, width: "100%" }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          loading={loading}
          disableSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
